import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, ChevronLeft, ChevronRight, Lock, LockOpen } from 'lucide-react';
import { useCashDay } from '../hooks/useCashDay';
import { money } from '../utils/quotes';
import { SOURCE_LABELS, bogotaToday, cashDifference, checkClose, dayLabel, differenceInfo, methodLabel, shiftDay } from '../utils/cashClose';

function Kpi({ label, value, hint, color = 'text-seryi-heading' }) {
  return (
    <div className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-4">
      <p className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">{label}</p>
      <p className={`text-xl font-black ${color}`}>{money(value)}</p>
      {hint && <p className="text-[10px] text-seryi-text mt-0.5">{hint}</p>}
    </div>
  );
}

function Rows({ title, rows, empty, label }) {
  return (
    <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-2">
      <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">{title}</h3>
      {rows.length === 0 ? <p className="text-xs text-seryi-text">{empty}</p> : rows.map((r) => (
        <div key={r.key} className="flex justify-between text-xs text-seryi-heading">
          <span>{label(r)}<span className="text-seryi-text"> · {r.count}</span></span>
          <span className="font-bold">{money(r.amount)}</span>
        </div>
      ))}
    </div>
  );
}

export default function CashClosePage() {
  const today = bogotaToday();
  const [day, setDay] = useState(today);
  const { summary, closings, loading, missing, closeDay, reopenDay } = useCashDay(day);
  const [counted, setCounted] = useState('');
  const [notes, setNotes] = useState('');
  const [reopening, setReopening] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setCounted(''); setNotes(''); setReopening(false); setReason(''); setError(''); }, [day]);

  if (missing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261009a_orders_accounting_bridge.sql</code> en Supabase para usar el cierre del día.</div>;
  }

  const closing = summary?.closing || null;
  const expected = Number(summary?.expected_cash ?? 0);
  const sources = (summary?.sold_by_source || []).map((r) => ({ key: r.source, ...r }));
  const collected = (summary?.collected_by_method || []).map((r) => ({ key: r.method, ...r }));
  const paidOut = (summary?.paid_by_method || []).map((r) => ({ key: r.method, ...r }));
  const staff = (summary?.cash_by_staff || []).map((r) => ({ key: r.staff_id || 'sin', ...r }));
  const liveDiff = counted === '' ? null : cashDifference(counted, expected);
  const info = liveDiff === null ? null : differenceInfo(liveDiff);
  const changed = closing && Number(closing.expected_cash) !== expected;

  const submit = async (e) => {
    e.preventDefault();
    const problem = checkClose({ counted, expected, notes });
    if (problem) { setError(problem); return; }
    setError('');
    setSaving(true);
    await closeDay(counted, notes);
    setSaving(false);
  };

  const reopen = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setError('Escribe el motivo para reabrir el día.'); return; }
    setError('');
    setSaving(true);
    const ok = await reopenDay(closing.id, reason);
    setSaving(false);
    if (ok) { setReopening(false); setReason(''); }
  };

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><ClipboardCheck size={20} /> Cierre del día</h2>
          <p className="text-xs text-seryi-text">Lo vendido y cobrado del día (pedidos, citas y ventas), lo que cada domiciliario debe entregar y el arqueo del efectivo. Lo acumulado está en <Link to="/v2/caja" className="text-seryi-green hover:underline">Caja y resultado</Link>.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" aria-label="Día anterior" onClick={() => setDay(shiftDay(day, -1))} className="p-2 border border-seryi-bg-light rounded-xl text-seryi-heading"><ChevronLeft size={14} /></button>
          <input type="date" aria-label="Día" max={today} value={day} onChange={(e) => e.target.value && e.target.value <= today && setDay(e.target.value)} className="bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading" />
          <button type="button" aria-label="Día siguiente" disabled={day >= today} onClick={() => setDay(shiftDay(day, 1))} className="p-2 border border-seryi-bg-light rounded-xl text-seryi-heading disabled:opacity-40"><ChevronRight size={14} /></button>
        </div>
      </div>
      <p className="text-xs text-seryi-heading capitalize font-bold">{dayLabel(day)}{closing && <span className="ml-2 normal-case text-emerald-400 font-bold inline-flex items-center gap-1"><Lock size={12} /> Día cerrado</span>}</p>

      {loading || !summary ? <p className="text-xs text-seryi-text">Cargando…</p> : (
        <>
          <section aria-label="Resumen del día" className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Vendido" value={summary.sold} hint={`${summary.sold_count} ventas`} />
            <Kpi label="Cobrado" value={summary.collected} color="text-emerald-400" hint="Todos los medios" />
            <Kpi label="Gastos pagados" value={summary.paid_out} color="text-rose-300" />
            <Kpi label="Efectivo esperado" value={expected} hint={`${money(summary.cash_in)} cobrado − ${money(summary.cash_out)} pagado`} color="text-seryi-green" />
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Rows title="Vendido por origen" rows={sources} empty="No hubo ventas este día." label={(r) => SOURCE_LABELS[r.key] || r.key} />
            <Rows title="Cobrado por medio de pago" rows={collected} empty="No hubo cobros este día." label={(r) => methodLabel(r.key)} />
            <Rows title="Efectivo por domiciliario o caja" rows={staff} empty="No hubo efectivo cobrado." label={(r) => r.name} />
            <Rows title="Gastos pagados por medio de pago" rows={paidOut} empty="No hubo gastos pagados." label={(r) => methodLabel(r.key)} />
          </div>

          <section aria-label="Pendientes" className="grid grid-cols-2 gap-3">
            <Kpi label="Quedó por cobrar del día" value={summary.receivable_day} hint={`${summary.receivable_day_count} ventas sin cobrar o con saldo`} color="text-amber-300" />
            <Kpi label="Por cobrar en total" value={summary.receivable_total} hint="Saldos de todas las ventas abiertas" color="text-amber-300" />
          </section>

          <section aria-label="Arqueo de efectivo" className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-3">
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">Arqueo de efectivo</h3>
            {closing ? (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-3 text-xs text-seryi-heading">
                  <div><p className="text-[10px] uppercase text-seryi-text font-bold">Esperado</p><p className="font-bold">{money(closing.expected_cash)}</p></div>
                  <div><p className="text-[10px] uppercase text-seryi-text font-bold">Contado</p><p className="font-bold">{money(closing.counted_cash)}</p></div>
                  <div><p className="text-[10px] uppercase text-seryi-text font-bold">Diferencia</p><p className={`font-bold ${differenceInfo(Number(closing.difference)).tone}`}>{differenceInfo(Number(closing.difference)).label}{Number(closing.difference) !== 0 ? ` ${money(Math.abs(Number(closing.difference)))}` : ''}</p></div>
                </div>
                {closing.notes && <p className="text-xs text-seryi-text">Notas: {closing.notes}</p>}
                <p className="text-[11px] text-seryi-text">Cerrado por {closing.closed_by || '—'} el {new Date(closing.closed_at).toLocaleString('es-CO')}.</p>
                {changed && <p role="alert" className="text-xs text-amber-300">Desde el cierre cambió el efectivo esperado (ahora {money(expected)}). Reabre el día y ciérralo de nuevo para que el cierre coincida.</p>}
                {reopening ? (
                  <form onSubmit={reopen} className="space-y-2">
                    <label className="block text-[11px] text-seryi-text" htmlFor="cash-reopen-reason">Motivo para reabrir el día</label>
                    <input id="cash-reopen-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading" />
                    <div className="flex gap-2">
                      <button type="submit" disabled={saving} className="bg-amber-400 text-seryi-bg-dark text-xs font-extrabold px-4 py-2 rounded-xl disabled:opacity-50">Reabrir día</button>
                      <button type="button" onClick={() => { setReopening(false); setError(''); }} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-4 py-2 rounded-xl">Cancelar</button>
                    </div>
                  </form>
                ) : (
                  <button type="button" onClick={() => setReopening(true)} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5"><LockOpen size={13} /> Reabrir el día</button>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <p className="text-xs text-seryi-text">Cuenta el efectivo que entregaron los domiciliarios y el de la caja. Esperado: <strong className="text-seryi-heading">{money(expected)}</strong>.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="cash-counted" className="block text-[11px] text-seryi-text mb-1">Efectivo contado</label>
                    <input id="cash-counted" inputMode="decimal" type="number" min="0" step="0.01" value={counted} onChange={(e) => setCounted(e.target.value)} className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading" />
                    {info && <p className={`text-[11px] mt-1 font-bold ${info.tone}`}>{info.label}{liveDiff !== 0 ? ` ${money(Math.abs(liveDiff))}` : ''}</p>}
                  </div>
                  <div>
                    <label htmlFor="cash-notes" className="block text-[11px] text-seryi-text mb-1">Notas {liveDiff ? '(obligatorias: hay diferencia)' : '(opcional)'}</label>
                    <input id="cash-notes" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading" />
                  </div>
                </div>
                <button type="submit" disabled={saving} className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-4 py-2 rounded-xl disabled:opacity-50">Cerrar día</button>
              </form>
            )}
            {error && <p role="alert" className="text-xs text-rose-300">{error}</p>}
          </section>

          <div className="overflow-auto rounded-3xl border border-seryi-bg-light">
            <table className="w-full text-xs text-seryi-heading" aria-label="Cierres anteriores">
              <thead className="bg-seryi-bg-dark text-seryi-text text-[10px] uppercase">
                <tr><th className="p-3 text-left">Día</th><th className="p-3 text-right">Esperado</th><th className="p-3 text-right">Contado</th><th className="p-3 text-right">Diferencia</th><th className="p-3 text-left">Cerró</th><th className="p-3 text-left">Estado</th></tr>
              </thead>
              <tbody>
                {closings.length === 0 && <tr><td colSpan={6} className="p-3 text-seryi-text">Aún no hay cierres.</td></tr>}
                {closings.map((c) => (
                  <tr key={c.id} className={`border-t border-seryi-bg-light ${c.voided_at ? 'opacity-50' : ''}`}>
                    <td className="p-3">{c.closing_date}</td>
                    <td className="p-3 text-right">{money(c.expected_cash)}</td>
                    <td className="p-3 text-right">{money(c.counted_cash)}</td>
                    <td className={`p-3 text-right ${differenceInfo(Number(c.difference)).tone}`}>{money(c.difference)}</td>
                    <td className="p-3">{c.closed_by || '—'}</td>
                    <td className="p-3">{c.voided_at ? `Reabierto: ${c.void_reason || ''}` : 'Cerrado'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
